import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AvatarService {
  private defaultAvatar = 'assets/layout/images/avatar.png';

  /**
   * Processes the raw avatar data:
   * 1. Trims whitespace and newlines.
   * 2. Returns default asset if empty/null/undefined.
   * 3. Leaves asset URLs unchanged.
   * 4. Leaves full data URIs unchanged.
   * 5. Otherwise prefixes as PNG base64.
   * @param avatar The raw avatar string (which might be null/undefined).
   * @returns The processed avatar URL.
   */
  processAvatar(avatar: string | ArrayBuffer | null | undefined): string {
    if (!avatar) {
      return this.defaultAvatar;
    }

    if (avatar instanceof ArrayBuffer) {
      return this.byteArrayToDataUri(avatar);
    }

    // Trim whitespace and newlines
    const processedAvatar = avatar.trim().replace(/\s+/g, '');

    // If already an asset path, return as is
    if (processedAvatar.startsWith('assets/') || processedAvatar.includes('/assets/')) {
      return processedAvatar;
    }

    // If already a data URI (any image type), return as is
    if (/^data:image\/[a-zA-Z]+;base64,/.test(processedAvatar)) {
      return processedAvatar;
    }

    // Otherwise, we assume raw base64 and prefix
    return 'data:image/png;base64,' + processedAvatar;
  }

  /**
   * Converts a byte array to a base64 data URI
   * @param bytes The byte array to convert
   * @param mimeType Optional MIME type (defaults to image/png)
   * @returns A data URI string or the default avatar if conversion fails
   */
  byteArrayToDataUri(bytes: ArrayBuffer | Uint8Array | null | undefined, mimeType: string = 'image/png'): string {
    if (!bytes || (bytes instanceof ArrayBuffer && bytes.byteLength === 0) || (bytes instanceof Uint8Array && bytes.length === 0)) {
      return this.defaultAvatar;
    }

    try {
      // Convert to Uint8Array if it's an ArrayBuffer
      const uint8Array = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;

      // Convert bytes to binary string
      const binary = Array.from(uint8Array)
        .map((b) => String.fromCharCode(b))
        .join('');

      // Convert to base64
      const base64 = btoa(binary);

      // Return as data URI
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error('Error converting byte array to data URI:', error);
      return this.defaultAvatar;
    }
  }
}
